import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, ArrowLeft, ExternalLink, FileText, BookOpen, X, Plus, Tag, CheckSquare, Send } from 'lucide-react';
import { EmptyState } from './EmptyState';
interface Doc {
  id: string;
  title: string;
  source: 'Confluence' | 'Google Drive';
  space: string;
  synced: string;
  snippet: string;
  content: string;
  labels: string[];
  parentPage?: string;
  fileType?: string;
  folder?: string;
}
const DOCS: Doc[] = [{
  id: '1',
  title: 'API Rate Limiting RFC',
  source: 'Confluence',
  space: 'Engineering',
  synced: '3h ago',
  snippet: 'This document outlines the proposed rate limiting strategy for all external-facing API endpoints...',
  labels: ['RFC', 'API'],
  parentPage: 'Technical Decisions',
  content: '# API Rate Limiting RFC\n\n## Summary\n\nThis document outlines the proposed rate limiting strategy for all external-facing API endpoints.\n\n## Background\n\nThird-party consumers of our `/sync` endpoint have been hitting 429 errors during peak hours.\n\n## Proposal\n\nImplement **token-bucket rate limiting** at the API gateway level:\n\n- **100 requests/minute** per API key\n- **10-request burst** allowance\n\n## Decision\n\n**Approved** by Tom and Sarah on 2026-04-05.'
}, {
  id: '2',
  title: 'Zenbot Architecture Overview',
  source: 'Confluence',
  space: 'Engineering',
  synced: 'yesterday',
  snippet: 'Zenbot is a voice-first AI assistant that captures ideas, relays notifications, and executes actions...',
  labels: ['Architecture'],
  parentPage: 'System Docs',
  content: '# Zenbot Architecture Overview\n\nZenbot is a voice-first AI assistant.\n\n## Components\n\n- **Web frontend** (Next.js)\n- **Voice agent** (Python)\n- **Data layer** (Turso/SQLite)\n- **Sync pipeline** (Nango + Convex)'
}, {
  id: '3',
  title: 'Runbook: Certificate Rotation',
  source: 'Confluence',
  space: 'Ops',
  synced: '2 days ago',
  snippet: 'Step-by-step guide for rotating SSL certificates across all services...',
  labels: ['Runbook', 'Ops'],
  parentPage: 'Runbooks',
  content: '# Runbook: Certificate Rotation\n\n## Steps\n\n1. Generate new certificate via Let\'s Encrypt\n2. Update Coolify SSL settings\n3. Verify all endpoints\n4. Update monitoring alerts'
}, {
  id: '4',
  title: 'Q2 Budget Proposal',
  source: 'Google Drive',
  space: 'Finance',
  synced: '4h ago',
  snippet: 'Proposed budget allocation for Q2 2026 including infrastructure, tooling, and contractor costs...',
  labels: ['Finance', 'Q2'],
  fileType: 'Spreadsheet',
  folder: 'Finance/2026',
  content: '# Q2 Budget Proposal\n\n## Infrastructure: \u00a31,200/month\n\n- Hetzner: \u00a3400\n- LiveKit: \u00a3300\n- Vercel: \u00a380'
}, {
  id: '5',
  title: 'Product Roadmap H1 2026',
  source: 'Google Drive',
  space: 'Product',
  synced: '1 day ago',
  snippet: 'Key milestones and deliverables for the first half of 2026...',
  labels: ['Product', 'Roadmap'],
  fileType: 'Document',
  folder: 'Product',
  content: '# Product Roadmap H1 2026\n\n## Q1\n\n- Voice agent MVP\n- Nango pipeline\n\n## Q2\n\n- Task review UI\n- Library and Data views'
}, {
  id: '6',
  title: 'Brand Guidelines',
  source: 'Google Drive',
  space: 'Design',
  synced: '3 days ago',
  snippet: 'Logo usage, colour palette, typography, and tone of voice guidelines...',
  labels: ['Design', 'Brand'],
  fileType: 'PDF',
  folder: 'Design/Brand',
  content: '# Brand Guidelines\n\n## Logo\n\nUse the full logo on light backgrounds.\n\n## Colours\n\n- Primary: #141418\n- Accent: #4a9a9a'
}];
const ALL_LABELS = ['RFC', 'API', 'Architecture', 'Runbook', 'Ops', 'Finance', 'Q2', 'Product', 'Roadmap', 'Design', 'Brand', 'Urgent', 'Review'];
const SOURCE_ICONS = {
  Confluence: FileText,
  'Google Drive': BookOpen
};
const TABS = ['All', 'Confluence', 'Google Drive'] as const;
const CONFLUENCE_FILTERS: Record<string, string[]> = {
  Space: ['Engineering', 'Ops', 'Product', 'Design'],
  'Parent Page': ['Technical Decisions', 'System Docs', 'Runbooks'],
  Labels: ['RFC', 'API', 'Architecture', 'Runbook', 'Ops']
};
const GDRIVE_FILTERS: Record<string, string[]> = {
  'File Type': ['Document', 'Spreadsheet', 'PDF', 'Presentation'],
  Folder: ['Finance/2026', 'Product', 'Design/Brand']
};
type FilterState = Record<string, string[]>;
function FilterPills({
  filters,
  activeFilters,
  onToggle
}: {
  filters: Record<string, string[]>;
  activeFilters: FilterState;
  onToggle: (c: string, v: string) => void;
}) {
  return <div className="flex flex-wrap gap-x-6 gap-y-3 mb-6" data-id="element-1223">
      {Object.entries(filters).map(([cat, vals]) => <div key={cat} className="flex items-center gap-2 flex-wrap" data-id="element-1224">
          <span className="text-[12px] text-[#8a8680]/50 uppercase tracking-wider" data-id="element-1225">{cat}</span>
          {vals.map(v => {
        const a = (activeFilters[cat] || []).includes(v);
        return <button key={v} onClick={() => onToggle(cat, v)} className={`text-[13px] px-2.5 py-1 rounded-full transition-colors ${a ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : 'bg-[#252530] text-[#8a8680] hover:text-[#e8e4df]'}`} data-id="element-1226">{v}{a && <X size={10} className="inline ml-1.5 -mt-0.5" data-id="element-1227" />}</button>;
      })}
        </div>)}
    </div>;
}
export function LibraryView() {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<string>('All');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<FilterState>({});
  const toggleFilter = (c: string, v: string) => {
    setActiveFilters(prev => {
      const cur = prev[c] || [];
      return {
        ...prev,
        [c]: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v]
      };
    });
  };
  if (viewingId) {
    const doc = DOCS.find(d => d.id === viewingId);
    if (doc) return <DocDetail doc={doc} onBack={() => setViewingId(null)} data-id="element-1228" />;
  }
  const currentFilters = activeTab === 'Confluence' ? CONFLUENCE_FILTERS : activeTab === 'Google Drive' ? GDRIVE_FILTERS : null;
  const hasActive = Object.values(activeFilters).some(v => v.length > 0);
  const filtered = DOCS.filter(d => {
    if (activeTab !== 'All' && d.source !== activeTab) return false;
    if (search && !d.title.toLowerCase().includes(search.toLowerCase()) && !d.snippet.toLowerCase().includes(search.toLowerCase())) return false;
    if (!hasActive) return true;
    for (const [cat, vals] of Object.entries(activeFilters)) {
      if (vals.length === 0) continue;
      if (cat === 'Space' && !vals.includes(d.space)) return false;
      if (cat === 'Parent Page' && d.parentPage && !vals.includes(d.parentPage)) return false;
      if (cat === 'Labels' && !d.labels.some(l => vals.includes(l))) return false;
      if (cat === 'File Type' && d.fileType && !vals.includes(d.fileType)) return false;
      if (cat === 'Folder' && d.folder && !vals.includes(d.folder)) return false;
    }
    return true;
  });
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] selection:bg-[#4a9a9a]/30 pt-12 pb-32 px-6" data-id="element-1229">
      <div className="max-w-[640px] mx-auto w-full flex flex-col" data-id="element-1230">
        <header className="mb-6" data-id="element-1231"><h1 className="text-[20px] font-bold text-[#e8e4df] tracking-tight mb-1" data-id="element-1232">Library</h1><p className="text-[#8a8680] text-[14px]" data-id="element-1233">Synced documents from Confluence and Google Drive.</p></header>
        <div className="relative mb-6" data-id="element-1234"><Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a8680]/50" data-id="element-1235" /><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search documents..." className="w-full bg-[#1c1c22] text-[#e8e4df] rounded-[12px] pl-11 pr-4 py-3 text-[15px] outline-none placeholder:text-[#8a8680]/40 focus:ring-1 focus:ring-[#4a9a9a]/30" data-id="element-1236" /></div>
        <nav className="flex items-center gap-6 mb-5 border-b border-[#8a8680]/10" data-id="element-1237">
          {TABS.map(tab => {
          const isActive = activeTab === tab;
          return <button key={tab} onClick={() => {
            setActiveTab(tab);
            setActiveFilters({});
          }} className={`pb-3 text-[14px] font-medium transition-colors relative ${isActive ? 'text-[#e8e4df]' : 'text-[#8a8680] hover:text-[#e8e4df]/80'}`} data-id="element-1238">{tab}{isActive && <motion.div layoutId="libraryTabIndicator" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#4a9a9a]" initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-1239" />}</button>;
        })}
        </nav>
        {currentFilters && <FilterPills filters={currentFilters} activeFilters={activeFilters} onToggle={toggleFilter} data-id="element-1240" />}
        {filtered.length === 0 ? <EmptyState message="No documents match." data-id="element-1241" /> : <div className="flex flex-col gap-3" data-id="element-1242">
            {filtered.map((doc, i) => {
          const Icon = SOURCE_ICONS[doc.source] || FileText;
          return <motion.button key={doc.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: i * 0.04
          }} onClick={() => setViewingId(doc.id)} className="bg-[#1c1c22] rounded-[16px] p-5 shadow-xl shadow-black/20 text-left group hover:bg-[#1e1e25] transition-colors w-full focus:outline-none focus:ring-2 focus:ring-[#4a9a9a]/40" data-id="element-1243">
                  <div className="flex items-start gap-4" data-id="element-1244">
                    <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0 mt-0.5" data-id="element-1245"><Icon size={18} className="text-[#8a8680]" data-id="element-1246" /></div>
                    <div className="flex-1 min-w-0" data-id="element-1247">
                      <h2 className="text-[#e8e4df] font-medium text-[15px]" data-id="element-1248">{doc.title}</h2>
                      <p className="text-[#8a8680]/60 text-[13px] mt-0.5" data-id="element-1249">{doc.space}{doc.fileType ? ` \u00b7 ${doc.fileType}` : ''}</p>
                      {doc.labels.length > 0 && <div className="flex items-center gap-1.5 mt-1.5" data-id="element-1250">{doc.labels.map(l => <span key={l} className="text-[11px] text-[#8a8680]/50 bg-[#252530] px-2 py-0.5 rounded-full" data-id="element-1251">{l}</span>)}</div>}
                      <p className="text-[#8a8680] text-sm mt-2 leading-[1.6] line-clamp-2" data-id="element-1252">{doc.snippet}</p>
                    </div>
                    <span className="text-[12px] text-[#8a8680]/40 whitespace-nowrap shrink-0" data-id="element-1253">{doc.synced}</span>
                  </div>
                </motion.button>;
        })}
          </div>}
      </div>
    </div>;
}
function DocDetail({
  doc,
  onBack
}: {
  doc: Doc;
  onBack: () => void;
}) {
  const Icon = SOURCE_ICONS[doc.source] || FileText;
  const [labels, setLabels] = useState(doc.labels);
  const [showLabelMenu, setShowLabelMenu] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskText, setTaskText] = useState('');
  const toggleLabel = (l: string) => setLabels(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]);
  const renderContent = (content: string) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('# ')) return <h1 key={i} className="text-[22px] font-bold text-[#e8e4df] mt-8 mb-4" data-id="element-1254">{line.slice(2)}</h1>;
      if (line.startsWith('## ')) return <h2 key={i} className="text-[18px] font-semibold text-[#e8e4df] mt-6 mb-3" data-id="element-1255">{line.slice(3)}</h2>;
      if (line.startsWith('- ')) return <li key={i} className="text-[#e8e4df]/80 text-[15px] leading-[1.8] ml-4" data-id="element-1256">{line.slice(2)}</li>;
      if (line.trim() === '') return <div key={i} className="h-2" data-id="element-1257" />;
      const bold = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#e8e4df]">$1</strong>');
      return <p key={i} className="text-[#e8e4df]/80 text-[15px] leading-[1.8]" dangerouslySetInnerHTML={{
        __html: bold
      }} data-id="element-1258" />;
    });
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] selection:bg-[#4a9a9a]/30 pt-12 pb-32 px-6" data-id="element-1259">
      <div className="max-w-[680px] mx-auto w-full" data-id="element-1260">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680] hover:text-[#e8e4df] transition-colors mb-8 focus:outline-none focus:ring-2 focus:ring-[#4a9a9a]/40 rounded" data-id="element-1261"><ArrowLeft size={16} data-id="element-1262" /><span className="text-sm" data-id="element-1263">Library</span></button>
        <header className="mb-6 flex items-start justify-between" data-id="element-1264">
          <div data-id="element-1265">
            <h1 className="text-[22px] font-bold text-[#e8e4df] tracking-tight" data-id="element-1266">{doc.title}</h1>
            <div className="flex items-center gap-3 mt-2" data-id="element-1267"><div className="flex items-center gap-1.5" data-id="element-1268"><Icon size={14} className="text-[#8a8680]/50" data-id="element-1269" /><span className="text-[13px] text-[#8a8680]/50" data-id="element-1270">{doc.source}</span></div><span className="text-[#8a8680]/20" data-id="element-1271">&middot;</span><span className="text-[13px] text-[#8a8680]/50" data-id="element-1272">{doc.space}</span><span className="text-[#8a8680]/20" data-id="element-1273">&middot;</span><span className="text-[13px] text-[#8a8680]/50" data-id="element-1274">Synced {doc.synced}</span></div>
          </div>
          <button className="flex items-center gap-1.5 text-[#8a8680]/40 hover:text-[#4a9a9a] transition-colors text-[13px]" data-id="element-1275"><ExternalLink size={14} data-id="element-1276" />View original</button>
        </header>

        <div className="flex items-center gap-2 flex-wrap mb-6" data-id="element-1277">
          <Tag size={14} className="text-[#8a8680]/40" data-id="element-1278" />
          {labels.map(l => <span key={l} className="inline-flex items-center gap-1.5 text-[12px] text-[#8a8680] bg-[#252530] px-2.5 py-1 rounded-full" data-id="element-1279">
              {l}<button onClick={() => toggleLabel(l)} className="hover:text-[#e8e4df] transition-colors" data-id="element-1280"><X size={9} data-id="element-1281" /></button>
            </span>)}
          <div className="relative" data-id="element-1282">
            <button onClick={() => setShowLabelMenu(!showLabelMenu)} className="text-[12px] text-[#8a8680]/40 hover:text-[#8a8680] transition-colors flex items-center gap-1" data-id="element-1283"><Plus size={12} data-id="element-1284" />Add</button>
            {showLabelMenu && <><div className="fixed inset-0 z-10" onClick={() => setShowLabelMenu(false)} data-id="element-1285" />
              <div className="absolute left-0 mt-2 rounded-lg shadow-xl overflow-hidden z-20 w-[160px] bg-[#252530] border border-[#8a8680]/10 max-h-[200px] overflow-y-auto" data-id="element-1286">
                {ALL_LABELS.filter(l => !labels.includes(l)).map(l => <button key={l} onClick={() => {
                toggleLabel(l);
                setShowLabelMenu(false);
              }} className="w-full text-left px-3 py-2 text-sm text-[#e8e4df] hover:bg-[#4a9a9a]/10 transition-colors" data-id="element-1287">{l}</button>)}
              </div></>}
          </div>
        </div>

        <div className="bg-[#1c1c22] rounded-[16px] p-8 shadow-xl shadow-black/20" data-id="element-1288">{renderContent(doc.content)}</div>

        <div className="mt-4" data-id="element-1289">
          {!taskOpen ? <button onClick={() => setTaskOpen(true)} className="flex items-center gap-2 text-[13px] text-[#8a8680] hover:text-[#e8e4df] transition-colors focus:outline-none focus:ring-2 focus:ring-[#4a9a9a]/40 rounded" data-id="element-1290"><CheckSquare size={14} data-id="element-1291" />Create task from this document</button> : <div className="flex items-end gap-3" data-id="element-1292">
              <textarea value={taskText} onChange={e => setTaskText(e.target.value)} placeholder={`Task linked to "${doc.title}"...`} className="flex-1 bg-[#1c1c22] text-[#e8e4df] rounded-[12px] px-4 py-3 text-[14px] outline-none placeholder:text-[#8a8680]/40 focus:ring-1 focus:ring-[#4a9a9a]/30 resize-none min-h-[80px]" autoFocus data-id="element-1293" />
              <button onClick={() => {
            setTaskText('');
            setTaskOpen(false);
          }} className="px-4 py-2.5 rounded-[10px] bg-[#4a9a9a] text-[#141418] text-[13px] font-medium hover:bg-[#5bb5b5] transition-colors shrink-0 focus:outline-none focus:ring-2 focus:ring-[#4a9a9a]/40" data-id="element-1294"><Send size={14} data-id="element-1295" /></button>
            </div>}
        </div>
      </div>
    </motion.div>;
}